// src/components/DoctorFormModal.tsx

import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, ModalContent, CloseButton, Button, Input } from '../styles/GlobalStyles';
import { addDoctor, updateDoctor } from '../services/firebase';
import { Doctor } from '../types';
import { useTranslation } from '../i18n';

interface DoctorFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDoctorAdded: () => void;
  doctor?: Doctor; // Optional for edit mode
}

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
`;

const FormColumn = styled.div`
  flex: 1;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background-color: white;
  
  &:focus {
    border-color: #0369a1;
    box-shadow: 0 0 0 3px rgba(3, 105, 161, 0.1);
    outline: none;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    border-color: #0369a1;
    box-shadow: 0 0 0 3px rgba(3, 105, 161, 0.1);
    outline: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 14px;
  margin-top: 8px;
`;

const SuccessMessage = styled.div`
  color: #166534;
  font-size: 14px;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  line-height: 1.4;
`;

const ExampleText = styled.span`
  display: block;
  margin-top: 2px;
  font-style: italic;
`;

const InstitutionInfo = styled.div`
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
`;

const DoctorFormModal: React.FC<DoctorFormModalProps> = ({ isOpen, onClose, onDoctorAdded, doctor }) => {
  const { t } = useTranslation();
  const institutionNumber = sessionStorage.getItem('institutionNumber') || '';
  const isEditMode = !!doctor;

  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: '남성' as '남성' | '여성',
    significant: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedDoctorNumber, setGeneratedDoctorNumber] = useState('');

  React.useEffect(() => {
    if (isOpen && doctor) {
      setFormData({
        name: doctor.name,
        age: doctor.age.toString(),
        gender: doctor.gender,
        significant: doctor.significant || ''
      });
      setGeneratedDoctorNumber(doctor.doctorNumber);
    } else if (isOpen) {
      // Reset form when opening in add mode
      setFormData({ name: '', age: '', gender: '남성', significant: '' });
      setGeneratedDoctorNumber('');
      setSuccess('');
      setErrors({});
    }
  }, [isOpen, doctor]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.name.trim()) newErrors.name = t('doctorForm.errors.nameRequired');
    if (!formData.age || parseInt(formData.age) <= 0 || parseInt(formData.age) > 150) {
      newErrors.age = t('doctorForm.errors.invalidAge');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSuccess('');
    try {
      if (isEditMode && doctor) {
        const updates: Partial<Doctor> = {
          name: formData.name.trim(),
          age: parseInt(formData.age),
          gender: formData.gender,
          significant: formData.significant.trim() ? formData.significant.trim() : ''
        };
        await updateDoctor(doctor.id, updates);
        setSuccess(t('doctorForm.success_edit'));
      } else {
        const doctorData: Omit<Doctor, 'id' | 'doctorNumber'> = {
          institutionNumber,
          name: formData.name.trim(),
          age: parseInt(formData.age),
          gender: formData.gender,
          ...(formData.significant.trim() && { significant: formData.significant.trim() })
        };
        const result = await addDoctor(doctorData);
        setGeneratedDoctorNumber(result.doctorNumber);
        setSuccess(t('doctorForm.success_add', { number: result.doctorNumber }));
      }
      onDoctorAdded(); // This will refresh the lists
      setTimeout(() => handleClose(), 2000);
    } catch (error) {
      console.error('의사 저장 오류:', error);
      setErrors({ general: isEditMode ? t('doctorForm.errors.editFailed') : t('doctorForm.errors.addFailed') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', age: '', gender: '남성', significant: '' });
    setErrors({});
    setSuccess('');
    setGeneratedDoctorNumber('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal onClick={handleClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>×</CloseButton>
        <h2 style={{ marginBottom: '24px', fontSize: '20px', fontWeight: '600' }}>
          {isEditMode ? t('doctorForm.modalTitle_edit') : t('doctorForm.modalTitle_add')}
        </h2>
        
        <InstitutionInfo>
          <div style={{ fontSize: '14px', color: '#0369a1', fontWeight: '500' }}>
            {t('doctorForm.institutionInfo', { number: institutionNumber })}
          </div>
        </InstitutionInfo>

        {!isEditMode ? (
          <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fed7aa', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' }}>
            <div style={{ fontSize: '14px', color: '#92400e', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {t('doctorForm.autoGenerate')}
            </div>
          </div>
        ) : (
          <div style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' }}>
             <div style={{ fontSize: '14px', color: '#374151', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' }}>
               의사번호: {generatedDoctorNumber}
             </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <FormRow>
            <FormColumn>
              <label htmlFor="name">{t('doctorForm.fields.name')}</label>
              <Input id="name" name="name" type="text" value={formData.name} onChange={handleInputChange} placeholder={t('doctorForm.placeholders.name')} disabled={isSubmitting}/>
              {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            </FormColumn>
            <FormColumn>
              <label htmlFor="age">{t('doctorForm.fields.age')}</label>
              <Input id="age" name="age" type="number" value={formData.age} onChange={handleInputChange} placeholder={t('doctorForm.placeholders.age')} min="1" max="150" disabled={isSubmitting}/>
              {errors.age && <ErrorMessage>{errors.age}</ErrorMessage>}
            </FormColumn>
          </FormRow>

          <FormGroup>
            <label htmlFor="gender">{t('doctorForm.fields.gender')}</label>
            <Select id="gender" name="gender" value={formData.gender} onChange={handleInputChange} disabled={isSubmitting}>
              <option value="남성">{t('gender.male')}</option>
              <option value="여성">{t('gender.female')}</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <label htmlFor="significant">{t('doctorForm.fields.significant')}</label>
            <TextArea id="significant" name="significant" value={formData.significant} onChange={handleInputChange} placeholder={t('doctorForm.placeholders.significant')} disabled={isSubmitting}/>
            <HelpText>
              {t('doctorForm.helpText.title')}
              <ExampleText>{t('doctorForm.helpText.example')}</ExampleText>
            </HelpText>
          </FormGroup>

          {errors.general && <ErrorMessage>{errors.general}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <ButtonGroup>
            <Button type="button" onClick={handleClose} disabled={isSubmitting}>{t('common.cancel')}</Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? t('common.saving') : t('common.save')}
            </Button>
          </ButtonGroup>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default DoctorFormModal;